import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { apiClient, handleApiError } from '@/infrastructure/api/client'
import { useAuthStore } from '@/application/stores/auth.store'
import { resolveLanguage } from '@/application/i18n/language'
import { investigationKeys } from '@/application/services/investigation.service'
import type {
  Fact,
  CreateFactFormData,
  UpdateFactFormData,
  FactScoreSummary,
  ScoreLetter,
} from '@/domains/types'

const factKeys = {
  // Locale-scoped for the same reason as investigationKeys.byId/read — see
  // that file (TRANSLATION-PLAN.md §9 Phase 1). `byLeadPrefix` (no locale)
  // is what mutations invalidate, so every locale variant cached for that
  // lead is dropped together.
  byLead: (leadId: string, locale: string) => ['facts', 'lead', leadId, locale] as const,
  byLeadPrefix: (leadId: string) => ['facts', 'lead', leadId] as const,
  // Locale-scoped for the same reason as byLead above — the management
  // facts list (privileged "all facts" read) must react to a language
  // switch just like the published-only read does.
  allByLead: (leadId: string, locale: string) => ['facts', 'lead', leadId, 'all', locale] as const,
  // No-locale prefix: what mutations invalidate, so every cached locale
  // variant of the "all facts" list is dropped together.
  allByLeadPrefix: (leadId: string) => ['facts', 'lead', leadId, 'all'] as const,
  byId: (entityId: string) => ['facts', entityId] as const,
  scores: (leadId: string, factId: string) => ['facts', 'scores', leadId, factId] as const,
}

export function useGetFactsByLead(leadId: string) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: factKeys.byLead(leadId, locale),
    queryFn: async (): Promise<Fact[]> => {
      const response = await apiClient.get<Fact[]>(`/leads/${leadId}/facts`, {
        params: { lang: locale },
      })
      return response.data
    },
    enabled: !!leadId,
    staleTime: 1000 * 60 * 1,
  })
}

export function useGetAllFactsByLead(leadId: string, options?: { refetchInterval?: number }) {
  const { i18n } = useTranslation()
  const locale = resolveLanguage(i18n.language)
  return useQuery({
    queryKey: factKeys.allByLead(leadId, locale),
    queryFn: async (): Promise<Fact[]> => {
      const response = await apiClient.get<Fact[]>(`/leads/${leadId}/facts/all`, {
        params: { lang: locale },
      })
      return response.data
    },
    enabled: !!leadId,
    staleTime: 1000 * 60 * 1,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * Raw, locale-independent read of every fact of a lead — no `?lang=` param,
 * so the backend never runs the translation fallback chain (TRANSLATION-PLAN.md
 * §6) and always returns the true source-language `title`/`description`.
 *
 * `useGetAllFactsByLead` passes the admin's own UI locale as `lang`, which is
 * correct for display but wrong for editing: when the admin's UI locale
 * differs from a fact's `sourceLocale`, that read returns locale-*resolved*
 * text in `title`/`description` (source text falls back to a translation, or
 * vice versa). `EditFactDialog` must seed its source-tab fields from the
 * actual source row, never from a resolved read — otherwise saving a
 * translation can silently overwrite the source columns with the resolved
 * (translated) text it was seeded with. This hook exists solely for that
 * dialog, mirroring `useGetLeadSource` in `lead.service.ts`.
 */
export function useGetFactSources(leadId: string) {
  return useQuery({
    queryKey: [...factKeys.allByLeadPrefix(leadId), 'source'] as const,
    queryFn: async (): Promise<Fact[]> => {
      const response = await apiClient.get<Fact[]>(`/leads/${leadId}/facts/all`)
      return response.data
    },
    enabled: !!leadId,
  })
}

export function useCreateFact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ url, ...data }: Omit<CreateFactFormData, 'leadId'>): Promise<Fact> => {
      const response = await apiClient.post<Fact>(`/leads/${leadId}/facts`, {
        ...data,
        // An empty source is sent as `null` so "no source" has a single representation.
        factUrl: url === undefined || url === '' ? null : url,
        leadId,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUpdateFact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id: entityId,
      url,
      ...data
    }: UpdateFactFormData & { id: string }): Promise<Fact> => {
      const body = url !== undefined ? { ...data, factUrl: url === '' ? null : url } : data
      const response = await apiClient.patch<Fact>(`/leads/${leadId}/facts/${entityId}`, body)
      return response.data
    },
    // Patches every cached fact list containing this fact SYNCHRONOUSLY
    // with the just-saved data (see the identical rationale on
    // `useUpdateLead.onSuccess` in lead.service.ts — `invalidateQueries`,
    // even awaited, only starts a real network refetch that can still lose
    // a race against the UI reopening the edit dialog), then invalidates
    // for eventual full consistency.
    onSuccess: async (result) => {
      const patchFact = (fact: Fact): Fact =>
        fact.id === result.id ? { ...fact, ...result } : fact

      queryClient.setQueriesData<Fact[]>({ queryKey: factKeys.byLeadPrefix(leadId) }, (previous) =>
        previous?.map(patchFact),
      )

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) }),
        // Refreshes the translation dashboard and its drill-down pages —
        // this fact's own completeness (and every ancestor's cascaded
        // aggregate) may have just changed.
        queryClient.invalidateQueries({ queryKey: ['translations'] }),
      ])
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useDeleteFact(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (entityId: string): Promise<{ success: boolean }> => {
      const response = await apiClient.delete<{ success: boolean }>(
        `/leads/${leadId}/facts/${entityId}`,
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function usePublishFact(leadId: string, investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (factId: string): Promise<Fact> => {
      const response = await apiClient.patch<Fact>(`/leads/${leadId}/facts/${factId}/publish`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: factKeys.allByLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(investigationId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useUnpublishFact(leadId: string, investigationId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (factId: string): Promise<Fact> => {
      const response = await apiClient.patch<Fact>(`/leads/${leadId}/facts/${factId}/unpublish`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: factKeys.allByLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: investigationKeys.byIdPrefix(investigationId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useReorderFacts(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (factIds: string[]): Promise<Fact[]> => {
      const response = await apiClient.patch<Fact[]>(`/leads/${leadId}/facts/reorder`, {
        factIds,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: factKeys.allByLeadPrefix(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useGetFactScoreSummary(
  leadId: string,
  factId: string,
  options?: { refetchInterval?: number },
) {
  return useQuery({
    queryKey: factKeys.scores(leadId, factId),
    queryFn: async (): Promise<FactScoreSummary> => {
      const response = await apiClient.get<FactScoreSummary>(
        `/leads/${leadId}/facts/${factId}/scores`,
      )
      return response.data
    },
    enabled: !!leadId && !!factId,
    staleTime: 1000 * 30,
    refetchInterval: options?.refetchInterval,
  })
}

/** Fetches score summaries for a list of facts in parallel, reusing the same cache. */
export function useGetAllFactScoreSummaries(leadId: string, factIds: string[]) {
  return useQueries({
    queries: factIds.map((factId) => ({
      queryKey: factKeys.scores(leadId, factId),
      queryFn: async (): Promise<FactScoreSummary> => {
        const response = await apiClient.get<FactScoreSummary>(
          `/leads/${leadId}/facts/${factId}/scores`,
        )
        return response.data
      },
      enabled: !!leadId && !!factId,
      staleTime: 1000 * 30,
    })),
  })
}

export function useSubmitFactScore(leadId: string, factId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (score: ScoreLetter): Promise<FactScoreSummary> => {
      const response = await apiClient.post<FactScoreSummary>(
        `/leads/${leadId}/facts/${factId}/scores`,
        { score },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.scores(leadId, factId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useValidateFactScore(leadId: string, factId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (score: ScoreLetter | null): Promise<Fact> => {
      const response = await apiClient.patch<Fact>(
        `/leads/${leadId}/facts/${factId}/scores/validate`,
        { score },
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: factKeys.scores(leadId, factId) })
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: factKeys.allByLeadPrefix(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useResetFactVotes(leadId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (factId: string): Promise<void> => {
      await apiClient.delete(`/leads/${leadId}/facts/${factId}/scores`)
    },
    onSuccess: (_data, factId) => {
      queryClient.invalidateQueries({ queryKey: factKeys.scores(leadId, factId) })
      queryClient.invalidateQueries({ queryKey: factKeys.byLeadPrefix(leadId) })
      queryClient.invalidateQueries({ queryKey: factKeys.allByLeadPrefix(leadId) })
    },
    onError: (error: unknown) => {
      throw new Error(handleApiError(error))
    },
  })
}

export function useIsPrivilegedForLead(leadId: string) {
  const { isAuthenticated, user } = useAuthStore()
  const isPrivilegedByRole = user?.role === 'admin' || user?.role === 'streamer'

  const query = useQuery({
    queryKey: ['facts', 'privilege', leadId] as const,
    queryFn: async (): Promise<boolean> => {
      const response = await apiClient.get<{ privileged: boolean }>(
        `/leads/${leadId}/privilege-check`,
      )
      return response.data.privileged
    },
    enabled: !!leadId && isAuthenticated && !isPrivilegedByRole,
    staleTime: 1000 * 60 * 5,
  })

  return {
    ...query,
    data: isPrivilegedByRole ? true : query.data,
    isLoading: isPrivilegedByRole ? false : isAuthenticated ? query.isLoading : false,
  }
}
